/**
 * `<SegmentedStatus>` — сегментированный переключатель одного статуса, где
 * выбранный сегмент подсвечен тоном (`.tone-*` из `surfaces.css`), а остальные
 * тихие. Одиночный выбор из 2–4 взаимоисключающих состояний в одной рамке —
 * не мульти-фильтр (это Pill-бар) и не цветной тег-статус (это `<Badge>`).
 *
 * `value = null` — ничего не выбрано (без дефолта): все сегменты тихие, пока
 * пользователь явно не выберет. Правило «когда сегментед-контрол» —
 * `patterns.md §StatusSegmentedControl`.
 *
 * `stretch` — растянуть контрол на всю ширину родителя и поделить его между
 * сегментами поровну. Нужен, когда контрол стоит в `flex-col`-колонке (форма,
 * панель): по умолчанию он `inline-flex`, родитель растягивает рамку, а
 * сегменты жмутся влево — рядом остаётся пустая рамка.
 *
 * Тон активного сегмента красится **классом** `.tone-*` (несёт bg + fg + 1px
 * border), не inline-`style` — иначе плоский против `<Badge>`-семейства
 * (`project_ui_tone_surface_utilities`). Маппинг «состояние → tone» держит
 * потребитель в `options`.
 */
import * as React from "react";

export interface SegmentedStatusOption<V extends string> {
  value: V;
  label: string;
  /** tone-класс для активного сегмента, напр. `"tone-success"`. */
  tone: string;
}

export interface SegmentedStatusProps<V extends string> {
  options: SegmentedStatusOption<V>[];
  /** Выбранное состояние или `null`, если ничего не выбрано (без дефолта). */
  value: V | null;
  onChange: (value: V) => void;
  ariaLabel: string;
  /** Растянуть на ширину родителя, сегменты — равными долями (см. шапку). */
  stretch?: boolean;
  /** Exact compact typography for fixed-height catalog rows. */
  size?: "default" | "catalog";
  "data-testid"?: string;
  id?: string;
  ref?: React.Ref<HTMLDivElement>;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
  "aria-describedby"?: string;
}

export function SegmentedStatus<V extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  stretch = false,
  size = "default",
  "data-testid": testId,
  id,
  ref,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: SegmentedStatusProps<V>): React.ReactElement {
  return (
    <div
      id={id}
      ref={ref}
      role="group"
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      tabIndex={id ? -1 : undefined}
      data-testid={testId}
      className={[
        "items-center gap-0.5 rounded-[var(--radius-button)] border border-[color:var(--rule-line)] p-0.5",
        ariaInvalid ? "border-[color:var(--alert-red)]" : "",
        stretch ? "flex w-full" : "inline-flex shrink-0",
      ].join(" ")}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            key={option.value}
            type="button"
            aria-pressed={active}
            data-testid={testId ? `${testId}-${option.value}` : undefined}
            onClick={() => onChange(option.value)}
            className={[
              // Тон активного сегмента красим МГНОВЕННО (без transition цвета/фона):
              // при ре-рендере родителя transition `background-color` застревает на
              // первом кадре и активный сегмент остаётся прозрачным (задача 89 §3.2).
              "rounded-[var(--radius-chip)] px-2 py-0.5 font-medium",
              size === "catalog" ? "text-[length:var(--fs-12)] leading-[16px]" : "text-xs",
              stretch ? "flex-1" : "",
              active
                ? option.tone
                : "text-muted-foreground hover:text-foreground border border-transparent",
            ].join(" ")}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
